import { ParamTypeDefinition, UIRouter } from '@uirouter/core';
import { STATE_ERROR_AUTHENTICATED, STATE_HOME_PAGE, STATE_LOGIN, STATE_MAIN } from './shared/app-states';
import { ResolveTypes, Transition } from '@uirouter/angular';
import { MainComponent } from './main/main.component';
import { MainParamsComponent } from './main/main-params.component';
import { Injector } from '@angular/core';
import { AppTransitionService } from './app-transition.service';
import { util } from './shared/util';
import { TranslationHelperService } from './shared/translation/translation-helper.service';
import { AuthService } from './shared/services/auth/auth.service';
import { GaStateDeclaration } from '@type_definitions/datatypes';

export enum RouterConfigType {
  RAW = 'rawQuery', // raw query parameters
  QUERY = 'query', // default which is being modified
  STRING = 'string'
}

export const MAIN_STATES:GaStateDeclaration[] = [
  {
    name: STATE_MAIN,
    url: '?hideTop&hideMenu&frameless&editMode&token&acslToken&noMenu&menu&errormessage&theme',
    params: {
      menu: {
        dynamic: true
      },
      errormessage: {
        dynamic: true
      },
      token: {
        dynamic: true
      },
      acslToken: {
        dynamic: true
      }
    },
    abstract: true,
    views: {
      main: {
        component: MainComponent
      }
    },
    resolve: [
      { token: 'translateReady', deps: [TranslationHelperService], resolveFn: (translationHelperService:TranslationHelperService) => translationHelperService.onReady() },
      { token: 'frameless', deps: [Transition], resolveFn: (transition:Transition) => transition.params()?.frameless === 'true' },
      { token: 'noMenu', deps: [Transition, AuthService], resolveFn: (transition:Transition, authService:AuthService) => transition.params()?.noMenu === 'true' || authService.getCurrentUser()?.noMenu },
      { token: 'hideTop', deps: [Transition], resolveFn: (transition:Transition) => transition.params()?.hideTop === 'true' || transition.params()?.hideTop === '1' },
      { token: 'hideMenu', deps: [Transition], resolveFn: (transition:Transition) => transition.params()?.hideMenu === 'true' || transition.params()?.hideMenu === '1' },
      { token: 'ga1frame', deps: [Transition], resolveFn: (transition:Transition) => transition.to()?.name === STATE_HOME_PAGE }
    ]
  },
  {
    name: STATE_HOME_PAGE,
    url: '/unacc?url',
    views: {
      params: {
        component: MainParamsComponent
      }
    },
    params: {
      url: {
        dynamic: true
      }
    },
    data: {
      noScrollTop: true
    }
  }
];

export function uiRouterConfigFn(router:UIRouter, injector:Injector):void {
  // we use our transition error handler
  router.stateService.defaultErrorHandler(util.noop);
  router.urlService.rules.initial({ state: STATE_LOGIN });
  router.urlService.rules.otherwise({
    state: STATE_ERROR_AUTHENTICATED,
    params: {
      invalidUrl: window.location.href
    }
  });

  const defaultQueryParamType:ParamTypeDefinition = router.urlService.config.type(RouterConfigType.QUERY);

  // save original query parameters into 'rawQuery' to be using for report filters
  const rawQueryParamType:ParamTypeDefinition = {
    ...defaultQueryParamType,
    decode: (paramValue:string) => defaultQueryParamType.decode(paramValue?.toString())
  };
  router.urlService.config.type(RouterConfigType.RAW, rawQueryParamType);

  // overwrite 'decoder' for default query parameter type
  // as for encoder, it uses default behavior from ui-router (return as-is)
  // see: https://ui-router.github.io/ng2/docs/5.0.0/interfaces/state.ng2statedeclaration.html#params / https://github.com/ui-router/core/blob/0329205db799e8ea1467b4142a35b3302aaacd86/src/params/paramTypes.ts#L272
  const newQueryParamType:ParamTypeDefinition = {
    ...defaultQueryParamType,
    decode: (paramValue:string) => defaultQueryParamType.decode(paramValue?.toString().replace(/\+/g, ' '))
  };
  router.urlService.config.type(RouterConfigType.QUERY, newQueryParamType);

  const defaultStringParamType:ParamTypeDefinition = router.urlService.config.type('string');
  const newStringParamType:ParamTypeDefinition = {
    ...defaultStringParamType,
    decode: (paramValue:string) => defaultStringParamType.decode(paramValue?.toString().replace(/\+/g, ' '))
  };
  router.urlService.config.type(RouterConfigType.STRING, newStringParamType);

  // transition hook listeners
  router.transitionService.onBefore({}, (transition:Transition):void => {
    const appTransitionService:AppTransitionService = injector.get(AppTransitionService);
    appTransitionService.onTransitionBefore(transition);
  });

  router.transitionService.onStart({}, (transition:Transition):void => {
    const appTransitionService:AppTransitionService = injector.get(AppTransitionService);
    appTransitionService.onTransitionStart(transition);
  });

  router.transitionService.onSuccess({}, (transition:Transition):void => {
    const appTransitionService:AppTransitionService = injector.get(AppTransitionService);
    appTransitionService.onTransitionSuccess(transition);
  });

  router.transitionService.onError({}, (transition:Transition):void => {
    const appTransitionService:AppTransitionService = injector.get(AppTransitionService);
    appTransitionService.onTransitionError(transition);
  });
}

/**
 * TRANSLATION_READY_RESOLVER is an object designed to block the rendering of the application
 * until the appropriate language settings have been determined and loaded into the user interface.
 * The resolveFn is an asynchronous function that utilizes TranslationHelperService to load necessary translation files
 * based on the data associated with the target state in the transition process.
 */
export const TRANSLATION_READY_RESOLVER:ResolveTypes = {
  token: 'translateReady',
  deps: [TranslationHelperService, Transition],
  // this is to block rendering of the app until the correct language has been decided and loaded in the UI
  resolveFn: (translationHelperService:TranslationHelperService, transition:Transition) => {
    return translationHelperService.loadTranslations(transition.to().data); // load additional translation files defined under data.modules
  }
};